// Copyright © Veeam Software Group GmbH

import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { MESSAGE_BOX_ICON_VIEW, MessageBox, useModal } from '@veeam/components';
import { merge } from 'rxjs';
import { first } from 'rxjs/operators';

import type { FC } from 'react';

import { useResources } from 'infrastructure/resources';
import { useLogin } from './hooks/useLogin';
import { Login } from './components/Login/Login';
import { operatorEventsHub } from 'api/signalr';

interface LoginContextState {
    username: string;
    logout(): Promise<void>;
}

const LoginContext = createContext<LoginContextState>({
    username: '',
    logout: () => new Promise(() => {}),
});

export const useLoginContext = (): LoginContextState => useContext(LoginContext);

export const LoginPage: FC = ({ children }) => {
    const resources = useResources();
    const [modal, action] = useModal({
        render: ({ deactivate }) => (
            <MessageBox
                header={resources.features.Main.productName}
                iconView={MESSAGE_BOX_ICON_VIEW.info}
                onRequestClose={deactivate}
                actions={[
                    {
                        text: 'OK',
                        onClick: deactivate,
                    },
                ]}
            >
                Some backup data is currently not available.
            </MessageBox>
        ),
    });
    const {
        username,
        logout,
        isAuthenticated,
        ...other
    } = useLogin();

    useEffect(() => {
        const onUnavailableData = merge(
            operatorEventsHub.onUnavailableExchangeDataFoundAsync,
            operatorEventsHub.onUnavailableOneDriveDataFoundAsync,
            operatorEventsHub.onUnavailableSharePointDataFoundAsync,
        );

        onUnavailableData
            .pipe(first())
            .subscribe(() => action.activate());
    }, []);

    useEffect(() => {
        if (!isAuthenticated && window.location.pathname !== '/') {
            window.history.replaceState({}, '', '/');
        }
    }, []);

    const loginContextValue = useMemo(() => ({
        username,
        logout,
    }), [username, logout]);

    return (
        <LoginContext.Provider value={loginContextValue}>
            {
                isAuthenticated
                    ? children
                    : <Login {...other} />
            }

            {modal}
        </LoginContext.Provider>
    );
};
